import { useEffect, useState } from "react";
import { Plus, FileDown, Trash2, Loader2, ClipboardCheck, Pencil } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { pdfUrl } from "../../lib/pdfUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import ImageUploadField from "../../components/ImageUploadField";
import SignedPdfCell from "../../components/SignedPdfCell";
import ReportSuccessPanel from "../../components/ReportSuccessPanel";
import { useAuth } from "../../context/AuthContext";

const PROCEDURE_REFERENCE = [
  "Welding of MS Parts",
  "Fitment of all Nozzles",
  "Fitment of Nitrogen Cartridge",
  "Fitment of all Hose",
  "Electrical Connections",
  "Control Unit Start",
];

const empty = {
  customer_name: "", site: "", project: "", po_no_date: "",
  vehicle_model: "", serial_number: "", equipment_name: "", type_of_equipment: "",
  installation_hmr: "", failure_hmr: "",
  installation_date: "", installed_by: "",
  remarks: "", image: "",
  // Engineer-captured procedure: name pre-filled with reference, detail blank.
  procedure_steps: PROCEDURE_REFERENCE.map((name) => ({ name, detail: "" })),
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-900 mb-3 pb-1 border-b border-slate-200">
      {title}
    </h3>
    {children}
  </div>
);

const Field = ({ label, value, onChange, required, type = "text", placeholder, rows, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">
      {label}{required && " *"}
    </Label>
    {rows ? (
      <Textarea rows={rows} required={required} value={value} onChange={onChange}
        placeholder={placeholder} className="rounded-none" data-testid={testId} />
    ) : (
      <Input type={type} required={required} value={value} onChange={onChange}
        placeholder={placeholder} className="rounded-none h-10" data-testid={testId} />
    )}
  </div>
);

export default function InstallationReportsAdmin() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/reports/installation").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateStep = (i, patch) => {
    const steps = [...form.procedure_steps];
    steps[i] = { ...steps[i], ...patch };
    setForm({ ...form, procedure_steps: steps });
  };

  const openNew = () => {
    setCreated(null);
    setEditingId(null);
    setForm({
      ...empty,
      procedure_steps: PROCEDURE_REFERENCE.map((name) => ({ name, detail: "" })),
      installed_by: user?.name || "",
      installation_date: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const openEdit = (r) => {
    setCreated(null);
    setEditingId(r.id);
    setForm({
      customer_name: r.customer_name || "",
      site: r.site || "",
      project: r.project || "",
      po_no_date: r.po_no_date || "",
      vehicle_model: r.vehicle_model || "",
      serial_number: r.serial_number || "",
      equipment_name: r.equipment_name || "",
      type_of_equipment: r.type_of_equipment || "",
      installation_hmr: r.installation_hmr || "",
      failure_hmr: r.failure_hmr || "",
      installation_date: r.installation_date || "",
      installed_by: r.installed_by || "",
      remarks: r.remarks || "",
      image: r.image || "",
      procedure_steps: (r.procedure_steps && r.procedure_steps.length
        ? r.procedure_steps
        : PROCEDURE_REFERENCE.map((name) => ({ name, detail: "" }))),
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        const { data } = await api.put(`/reports/installation/${editingId}`, form);
        toast.success(`Report ${data.report_no} updated`);
        setCreated(data);
      } else {
        const { data } = await api.post("/reports/installation", form);
        toast.success(`Report ${data.report_no} created`);
        setCreated(data);
      }
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const closeDialog = () => { setOpen(false); setCreated(null); setEditingId(null); load(); };

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.report_no}?`)) return;
    if (user.role !== "admin") return toast.error("Admin only");
    try {
      await api.delete(`/reports/installation/${r.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-installation-reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Field Reports</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6 text-amber-500" /> Installation Reports
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Captures customer, vehicle, equipment & install details — engineers fill the
            procedure steps with their own observations during fitment.
          </p>
        </div>
        <Button data-testid="new-installation-report-btn" onClick={openNew} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New Report
        </Button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3 font-semibold">Report No</th>
              <th className="text-left p-3 font-semibold">Customer</th>
              <th className="text-left p-3 font-semibold">Vehicle / Equipment</th>
              <th className="text-left p-3 font-semibold">Serial</th>
              <th className="text-left p-3 font-semibold">Installer</th>
              <th className="text-left p-3 font-semibold">Date</th>
              <th className="text-left p-3 font-semibold">Signed PDF</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-200">
                <td className="p-3 font-mono text-xs">{r.report_no}</td>
                <td className="p-3 font-semibold">{r.customer_name}<div className="text-xs text-slate-500 font-normal">{r.site}</div></td>
                <td className="p-3">{r.vehicle_model || r.equipment_name}<div className="text-xs text-slate-500">{r.type_of_equipment || r.equipment_name}</div></td>
                <td className="p-3 font-mono text-xs text-slate-600">{r.serial_number}</td>
                <td className="p-3">{r.installed_by}</td>
                <td className="p-3 font-mono text-xs">{r.installation_date}</td>
                <td className="p-3"><SignedPdfCell report={r} endpoint="/reports/installation" onUploaded={load} /></td>
                <td className="p-3 text-right space-x-1">
                  {r.pdf_url && (
                    <a href={pdfUrl(r)} target="_blank" rel="noopener noreferrer"
                       className="inline-flex p-2 hover:bg-slate-100" title="Download PDF">
                      <FileDown className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => openEdit(r)}
                    className="inline-flex p-2 hover:bg-amber-50 text-amber-700"
                    title="Edit & regenerate PDF"
                    data-testid={`edit-installation-${r.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {user.role === "admin" && (
                    <button onClick={() => del(r)} className="p-2 hover:bg-red-50 text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-slate-500 text-sm">No reports yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
        <DialogContent className="max-w-3xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {created ? "Installation Report Saved" : (editingId ? "Edit Installation Report" : "New Installation Report")}
            </DialogTitle>
          </DialogHeader>
          {created ? (
            <ReportSuccessPanel report={created} endpoint="/reports/installation"
              onDone={closeDialog} onUploaded={load} />
          ) : (
          <form onSubmit={submit} className="mt-2" data-testid="installation-report-form">
            <Section title="Customer">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Customer Name" required value={form.customer_name} onChange={setField("customer_name")} testId="ins-customer" />
                <Field label="Site / Location" required value={form.site} onChange={setField("site")} testId="ins-site" />
                <Field label="Project" value={form.project} onChange={setField("project")} placeholder="e.g. WCL AFDSS Rollout Phase II" />
                <Field label="P.O No. & Date" value={form.po_no_date} onChange={setField("po_no_date")} placeholder="e.g. WCL/PUR/2026-114 dated 28/03/2026" />
              </div>
            </Section>

            <Section title="Vehicle & Equipment">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Vehicle Model" value={form.vehicle_model} onChange={setField("vehicle_model")} placeholder="e.g. Komatsu HD785-7" />
                <Field label="Vehicle SI No." required value={form.serial_number} onChange={setField("serial_number")} />
                <Field label="Type of Equipment" required value={form.equipment_name} onChange={setField("equipment_name")} placeholder="e.g. AFDSS Dual-Agent" />
                <Field label="Equipment Variant" value={form.type_of_equipment} onChange={setField("type_of_equipment")} placeholder="e.g. 6kg DCP + 9L AFFF" />
                <Field label="Installation HMR" value={form.installation_hmr} onChange={setField("installation_hmr")} placeholder="Hours/Meter at install" />
                <Field label="Failure HMR" value={form.failure_hmr} onChange={setField("failure_hmr")} placeholder="Hours/Meter at failure (if any)" />
              </div>
            </Section>

            <Section title="Installation">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Installation Date" type="date" required value={form.installation_date} onChange={setField("installation_date")} testId="ins-date" />
                <Field label="Installed By (Field Engineer)" required value={form.installed_by} onChange={setField("installed_by")} testId="ins-installer" />
              </div>
            </Section>

            <Section title="Procedure — Field-Filled (each step engineer-captured)">
              <p className="text-xs text-slate-500 mb-3">
                Reference: standard 6-step procedure. Fill the <strong>Details</strong> for what was actually
                performed on this installation. The PDF prints exactly what you write here.
              </p>
              <div className="border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-white text-xs uppercase">
                    <tr>
                      <th className="p-2 w-10">#</th>
                      <th className="p-2 text-left w-56">Procedure</th>
                      <th className="p-2 text-left">Details (engineer to fill)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.procedure_steps.map((s, i) => (
                      <tr key={i} className="border-t border-slate-200">
                        <td className="p-2 font-mono text-xs text-center align-top pt-3">{i + 1}</td>
                        <td className="p-1 align-top">
                          <Input value={s.name} onChange={(e) => updateStep(i, { name: e.target.value })}
                            className="rounded-none h-10" />
                        </td>
                        <td className="p-1">
                          <Textarea rows={2} value={s.detail}
                            onChange={(e) => updateStep(i, { detail: e.target.value })}
                            placeholder="Describe what was done for this step…"
                            className="rounded-none resize-y min-h-[44px]" data-testid={`ins-step-${i}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Remarks & Photo">
              <Field label="Remarks" rows={4} value={form.remarks} onChange={setField("remarks")}
                placeholder="Any observations, additional procedures, deviations..." />
              <div className="mt-4">
                <ImageUploadField label="Installation Photo" value={form.image}
                  onChange={(v) => setForm({ ...form, image: v })} testId="ins-photo" />
              </div>
              <div className="mt-4 bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800">
                <strong>Signatures:</strong> After generating this PDF, print it, collect Field Engineer &amp;
                Customer signatures, scan and upload using the <em>Upload Signed PDF</em> button.
              </div>
            </Section>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={closeDialog} className="rounded-none">Cancel</Button>
              <Button data-testid="ins-submit" type="submit" disabled={busy} className="rounded-none btn-amber font-semibold">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingId ? "Save & Regenerate PDF" : "Submit & Generate PDF"}
              </Button>
            </div>
          </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
